import { WebSocketContext } from '@/contexts/WebSocketContext';
import { useGameFeature } from '@/hooks/useGameFeature';
import { CMDPsBetLogFailQuery, CMDPsBetLogQuery } from '@/models/cmd/live';
import {
    getBetFailSummaryReportState,
    getBetSummaryReportState,
} from '@/modules/report/selector';
import { FailSummary } from '@/modules/report/slice/betFailSummary';
import { Summary } from '@/modules/report/slice/betSummary';
import { RootState } from '@/store/store';
import { ArrowForwardIosOutlined } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AmountText } from '../../../../utils/formatter';
import { NestedDialog } from '../NestedDialog';
import { ReportDivider } from '../ReportDivider';
import { BetRecordDetailsReport } from './BetRecordDetailsReport';
import './index.scss';

type CloseHandler = () => void;

type Props = {
    onClose: CloseHandler;
    playButtonAudio: CloseHandler;
    startDate: string;
    endDate: string;
    isFailed?: boolean;
};

export const BetRecordReport = (props: Props) => {
    const {
        onClose,
        playButtonAudio,
        startDate,
        endDate,
        isFailed = false,
    } = props;
    const [collapseIndex, setCollapseIndex] = useState(-1);
    const [levelIndex, setLevelIndex] = useState(-1);
    const { summaries: successfulSummaries } = useSelector((state: RootState) =>
        getBetSummaryReportState(state)
    );
    const { summaries: betFailSummaries } = useSelector((state: RootState) =>
        getBetFailSummaryReportState(state)
    );
    const { t } = useTranslation();
    const { isAutoConfirmEnabled } = useGameFeature();
    const { sendCommand } = useContext(WebSocketContext);

    const [summaries, setSummaries] = useState<Summary[] | FailSummary[]>();

    useEffect(() => {
        setSummaries(isFailed ? betFailSummaries : successfulSummaries);
    }, [isFailed, successfulSummaries, betFailSummaries]);

    const requestBetRecordDetail = (dateStr: string) => {
        // console.warn('app::report::betrecord::summaries: ', summaries);
        // const startDate = summaries[summaries.length - 1].ReportDate;
        // const endDate = summaries[0].ReportDate;
        // console.warn('app::report::betrecord::startDate: ', startDate);
        // console.warn('app::report::betrecord::endDate: ', endDate);
        // console.warn('app::report::betrecord::dateStr: ', dateStr);

        let from,
            to = '';
        if (dateStr === startDate) {
            from = `${dateStr} 12:00:00`;
            to = `${moment(dateStr, 'YYYY-MM-DD').format('YYYY-MM-DD')} 23:59:59`;
        } else if (dateStr === endDate) {
            from = `${dateStr} 00:00:00`;
            to = `${moment(dateStr, 'YYYY-MM-DD').format('YYYY-MM-DD')} 11:59:59`;
        } else {
            from = `${dateStr} 00:00:00`;
            to = `${dateStr} 23:59:59`;
        }
        if (isFailed) {
            const request = new CMDPsBetLogFailQuery();
            request.FromDate = from;
            request.ToDate = to;
            sendCommand(request);
        } else {
            const request = new CMDPsBetLogQuery();
            request.FromDate = from;
            request.ToDate = to;
            sendCommand(request);
        }
    };

    return (
        <>
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                padding="0.5rem"
                paddingRight={'2.5rem'}
                color="#BF9A5D"
                className="bet-record-report"
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="flex-start"
                    sx={{ width: '30%' }}
                >
                    {t('betlog.date')}
                </Grid>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ width: '30%' }}
                >
                    {t('betlog.bet_amount')}
                </Grid>
                {!isFailed && (
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ width: '30%' }}
                    >
                        {t('betlog.win_lose')}
                    </Grid>
                )}
            </Grid>
            <ReportDivider />
            {summaries && summaries.length > 0 ? (
                <List sx={{ paddingY: 0 }}>
                    {Object.keys(summaries).map(key => {
                        const ci = Number(key);
                        const isCollapseOpen = collapseIndex === ci;
                        const resultAmount = summaries[ci].ResultAmount ?? 0;
                        const date = summaries[ci].ReportDate ?? '-';
                        // let winLoseLabel: string = '';
                        // if (resultAmount < 0) winLoseLabel = t('betlog.lose');
                        // if (resultAmount > 0) winLoseLabel = t('betlog.win');
                        // const rollingAmount = summaries[ci].RollingAmount ?? 0;
                        const turnoverAmount =
                            summaries[ci].TurnOverAmount ?? 0;
                        return (
                            <div key={`list-${date}`}>
                                <ListItemButton
                                    onClick={() => {
                                        requestBetRecordDetail(date);
                                        setCollapseIndex(ci);
                                        setLevelIndex(0);
                                    }}
                                    selected={isCollapseOpen}
                                    className="bet-record-report-list-item"
                                >
                                    <ListItemText>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                width="100%"
                                            >
                                                <Grid
                                                    item
                                                    width="30%"
                                                    display="flex"
                                                    justifyContent={
                                                        'flex-start'
                                                    }
                                                    className="bet-record-report"
                                                >
                                                    {date}
                                                </Grid>
                                                <Grid
                                                    item
                                                    width="30%"
                                                    display="flex"
                                                    justifyContent={'flex-end'}
                                                    className="bet-record-report"
                                                >
                                                    {/* <BaseTypography
                                                        resize={{
                                                            direction:
                                                                'horizontal',
                                                            value: '26vw',
                                                        }}
                                                    >
                                                        {(
                                                            turnoverAmount / 100
                                                        ).toLocaleString(
                                                            'en-US',
                                                            {
                                                                maximumFractionDigits: 2,
                                                                minimumFractionDigits: 2,
                                                            }
                                                        )}
                                                    </BaseTypography> */}
                                                    <AmountText
                                                        amount={
                                                            turnoverAmount / 100
                                                        }
                                                        textAlign={'end'}
                                                        with_color={false}
                                                        with_sign={false}
                                                    />
                                                </Grid>
                                                {!isFailed && (
                                                    <Grid
                                                        container
                                                        direction={'column'}
                                                        width="30%"
                                                        className="bet-record-report"
                                                    >
                                                        {/* <Grid
                                                        item
                                                        width="100%"
                                                        display="flex"
                                                        justifyContent={'flex-end'}
                                                    >
                                                        {(rollingAmount/100).toLocaleString('en-US', {maximumFractionDigits: 2,minimumFractionDigits: 2})}
                                                    </Grid> */}
                                                        <Grid
                                                            item
                                                            width="100%"
                                                            display="flex"
                                                            justifyContent={
                                                                'flex-end'
                                                            }
                                                        >
                                                            <AmountText
                                                                amount={
                                                                    resultAmount /
                                                                    100
                                                                }
                                                                textAlign={
                                                                    'end'
                                                                }
                                                                with_color={
                                                                    true
                                                                }
                                                                with_sign={true}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </Stack>
                                            <Grid
                                                item
                                                display="flex"
                                                alignItems="baseline"
                                                margin="0 0 0 0.5rem"
                                            >
                                                <ArrowForwardIosOutlined
                                                    sx={{
                                                        color: '#BF9A5D',
                                                    }}
                                                />
                                            </Grid>
                                        </Stack>
                                    </ListItemText>
                                </ListItemButton>
                                <ReportDivider isCell={true} />
                                <NestedDialog
                                    isOpen={
                                        levelIndex >= 0 && collapseIndex === ci
                                    }
                                    onClose={() => {
                                        onClose();
                                    }}
                                    currentIndex={levelIndex}
                                    setCurrentIndex={setLevelIndex}
                                    playButtonAudio={playButtonAudio}
                                    title={
                                        isAutoConfirmEnabled
                                            ? isFailed
                                                ? 'betlog.unsuccessful'
                                                : 'betlog.successful'
                                            : ''
                                    }
                                >
                                    <BetRecordDetailsReport
                                        onClose={() => onClose()}
                                        currentIndex={levelIndex}
                                        setCurrentIndex={setLevelIndex}
                                        playButtonAudio={playButtonAudio}
                                        date={date}
                                        isFailed={isFailed}
                                    />
                                </NestedDialog>
                            </div>
                        );
                    })}
                </List>
            ) : (
                <Grid
                    item
                    container
                    justifyContent="center"
                    sx={{ margin: '1rem 0' }}
                >
                    {t('betlog.noRecord')}
                </Grid>
            )}
        </>
    );
};
